import { memo } from 'react';
import { Box, makeStyles, Checkbox } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SText } from '@setvi/shared/components';
import { DragHandleIcon } from '@setvi/shared/components/sicons';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#F8F9FA',
    borderRadius: '16px',
    padding: '0 12px',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  selected: {
    backgroundColor: fade(theme.palette.primary.main, 0.1),
    // Inset shadow draws a crisp border on every side without shifting layout
    // (and isn't clipped by an ancestor's overflow:hidden like an outline).
    boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`
  },
  checkbox: {
    padding: '4px',
    color: '#5D7083'
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '40px',
    color: '#5D7083',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    padding: '8px 0'
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#333D47',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  isDragging: {
    opacity: 0.5
  },
  dragOverlay: {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#FFFFFF'
  }
}));

interface DraggableItemProps {
  item: QuoteProductDTO;
  isDragOverlay?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

/**
 * Draggable product item component
 * Used for both root-level products and products within sections
 */
export const DraggableItem = memo(
  ({
    item,
    isDragOverlay = false,
    selectable = false,
    selected = false,
    onToggleSelect
  }: DraggableItemProps) => {
    const classes = useStyles();
    const itemId = item.id || item._id;

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: itemId,
      disabled: isDragOverlay
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${classes.container} ${selected ? classes.selected : ''} ${isDragging ? classes.isDragging : ''} ${isDragOverlay ? classes.dragOverlay : ''}`}
        {...attributes}
        {...listeners}>
        {selectable && (
          // Stop pointer events reaching the sortable listeners so ticking the
          // box never starts a drag.
          <Box
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}>
            <Checkbox
              size="small"
              color="primary"
              className={classes.checkbox}
              checked={selected}
              onChange={() => onToggleSelect?.(itemId)}
            />
          </Box>
        )}
        <Box className={classes.dragHandle}>
          <DragHandleIcon style={{ fontSize: '12px' }} />
        </Box>
        <Box className={classes.content}>
          <SText
            title={item.name || 'Unnamed Product'}
            weight="semibold"
            className={classes.title}
            maxLines={1}
          />
        </Box>
      </div>
    );
  }
);
